<template>
  <div>
    <q-dialog v-model="wizardDialog" @hide="onWizardDialogHide">
      <q-card class="wizard-card" style="width: 950px; max-width: 95vw">
        <q-toolbar>
          <q-toolbar-title class="wizard-dialog-title">{{
            isEditMode ? 'Edit Pig Run' : 'New Pig Run Wizard'
          }}</q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <!-- Wizard Step Indicator -->
        <div class="wizard-stepper q-px-lg q-pt-md q-pb-sm">
          <div class="row items-center justify-center no-wrap">
            <template
              v-for="(stepDef, idx) in wizardStepDefinitions"
              :key="'step-' + stepDef.step"
            >
              <div
                class="wizard-stepper-item column items-center"
                :class="{
                  'wizard-stepper-active': wizardStep === stepDef.step,
                  'wizard-stepper-completed': isStepCompleted(stepDef.step),
                  'wizard-stepper-clickable':
                    stepDef.step <= wizardMaxStepReached,
                  'wizard-stepper-disabled':
                    stepDef.step > wizardMaxStepReached,
                }"
                @click="onWizardGoToStep(stepDef.step)"
              >
                <div class="wizard-stepper-circle">
                  <q-icon
                    v-if="isStepCompleted(stepDef.step)"
                    name="check"
                    size="16px"
                  />
                  <span v-else>{{ idx + 1 }}</span>
                </div>
                <div class="wizard-stepper-label q-mt-xs">
                  {{ stepDef.label }}
                </div>
              </div>
              <div
                v-if="idx < wizardStepDefinitions.length - 1"
                class="wizard-stepper-line"
                :class="{
                  'wizard-stepper-line-completed': isStepCompleted(
                    stepDef.step
                  ),
                }"
              />
            </template>
          </div>
        </div>
        <q-separator />

        <!-- Step 1: Run type selection -->
        <q-card-section v-if="wizardStep === 1">
          <div class="wizard-step-title q-mb-md">
            What type of pig run would you like to create?
          </div>
          <div class="column q-gutter-sm">
            <q-radio
              v-model="wizardRunType"
              val="live-tracked"
              label="Live-Tracked"
              color="blue-brand"
            />
            <div
              v-if="wizardRunType === 'live-tracked'"
              class="q-ml-lg q-mb-sm wizard-body-text text-grey-8"
            >
              A Live-Tracked Pig Run represents an active pipeline inspection
              run where the pig's location is continuously updated in real time
              as it moves through the pipeline. The system ingests live passage
              detections from Above Ground Markers (AGMs) and dynamically
              calculates the pig's estimated position along the pipeline path.
              <br /><br />
              The map view becomes the operational control center.
            </div>
            <q-radio
              v-model="wizardRunType"
              val="reconstructed"
              label="Reconstructed"
              color="blue-brand"
            />
            <div
              v-if="wizardRunType === 'reconstructed'"
              class="q-ml-lg q-mb-sm wizard-body-text text-grey-8"
            >
              Creates a completed pig run by associating previously transmitted
              passage detections with the defined pipeline markers. This run
              type does not track the pig in real time, but instead reconstructs
              the pig's path and timing using stored detection data.
            </div>
            <q-radio
              v-model="wizardRunType"
              val="unknown"
              label="I don't know"
              color="blue-brand"
            />
            <div
              v-if="wizardRunType === 'unknown'"
              class="q-ml-lg q-mb-sm wizard-body-text text-grey-8"
            ></div>
          </div>
        </q-card-section>

        <!-- Step 2: General details (Live-Tracked) -->
        <q-card-section
          v-if="wizardStep === 2 && wizardRunType !== 'reconstructed'"
        >
          <div class="wizard-step-title q-mb-md">General</div>
          <div class="row q-gutter-md">
            <div class="col" style="max-width: 500px">
              <div class="q-gutter-md">
                <q-input
                  v-model="wizardName"
                  dense
                  outlined
                  color="blue-brand"
                  label="Name"
                  maxlength="500"
                  :rules="[
                    (val) => !!val || 'Name is required.',
                    (val) =>
                      (val && val.length <= 500) ||
                      'Name must be 500 characters or less.',
                  ]"
                />
                <div class="row items-center q-gutter-sm">
                  <q-select
                    v-model="wizardPipelineSection"
                    dense
                    outlined
                    color="blue-brand"
                    label="Pipeline"
                    :options="wizardPipelineSectionOptions"
                    clearable
                    class="col"
                  />
                  <q-btn
                    flat
                    dense
                    round
                    icon="settings"
                    color="blue-brand"
                    @click="onManagePipelineSectionOpen"
                  >
                    <q-tooltip>Create or update pipelines</q-tooltip>
                  </q-btn>
                </div>
                <q-input
                  v-model="wizardJobNumber"
                  dense
                  outlined
                  color="blue-brand"
                  label="Job Number"
                  maxlength="50"
                  :rules="[
                    (val) =>
                      !val ||
                      val.length <= 50 ||
                      'Job Number must be 50 characters or less.',
                  ]"
                />
                <q-input
                  v-model="wizardNote"
                  dense
                  outlined
                  color="blue-brand"
                  label="Note"
                  type="textarea"
                  autogrow
                />
              </div>
            </div>
            <div class="col">
              <div
                v-show="wizardPipelineSection"
                ref="wizardPreviewMap"
                style="
                  height: 220px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
              <div
                v-if="!wizardPipelineSection"
                class="flex items-center justify-center text-grey-5 text-caption text-center"
                style="
                  height: 220px;
                  border: 1px dashed #ccc;
                  border-radius: 4px;
                "
              >
                Select a pipeline to preview markers
              </div>
            </div>
          </div>
        </q-card-section>

        <!-- Step 2: General details (Reconstructed) -->
        <q-card-section
          v-if="wizardStep === 2 && wizardRunType === 'reconstructed'"
        >
          <div class="wizard-step-title q-mb-md">General</div>
          <div class="row q-gutter-md">
            <div class="col" style="max-width: 500px">
              <div class="q-gutter-md">
                <q-input
                  v-model="wizardName"
                  dense
                  outlined
                  color="blue-brand"
                  label="Name"
                  maxlength="500"
                  :rules="[
                    (val) => !!val || 'Name is required.',
                    (val) =>
                      (val && val.length <= 500) ||
                      'Name must be 500 characters or less.',
                  ]"
                />

                <div class="row items-center q-gutter-sm">
                  <q-select
                    v-model="wizardPipelineSection"
                    dense
                    outlined
                    color="blue-brand"
                    label="Pipeline"
                    :options="wizardPipelineSectionOptions"
                    clearable
                    class="col"
                  />
                  <q-btn
                    flat
                    dense
                    round
                    icon="settings"
                    color="blue-brand"
                    @click="onManagePipelineSectionOpen"
                  >
                    <q-tooltip>Create or update pipelines</q-tooltip>
                  </q-btn>
                </div>

                <q-input
                  v-model="wizardJobNumber"
                  dense
                  outlined
                  color="blue-brand"
                  label="Job Number"
                  maxlength="50"
                  :rules="[
                    (val) =>
                      !val ||
                      val.length <= 50 ||
                      'Job Number must be 50 characters or less.',
                  ]"
                />

                <q-input
                  v-model="wizardNote"
                  dense
                  outlined
                  color="blue-brand"
                  label="Note"
                  type="textarea"
                  autogrow
                />
              </div>
            </div>
            <div class="col">
              <div
                v-show="wizardPipelineSection"
                ref="wizardPreviewMapR"
                style="
                  height: 220px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
              <div
                v-if="!wizardPipelineSection"
                class="flex items-center justify-center text-grey-5 text-caption text-center"
                style="
                  height: 220px;
                  border: 1px dashed #ccc;
                  border-radius: 4px;
                "
              >
                Select a pipeline to preview markers
              </div>
            </div>
          </div>
        </q-card-section>

        <!-- Step 3: AGMs (Live-Tracked) -->
        <q-card-section v-if="wizardStep === 3">
          <div class="wizard-step-title q-mb-md">AGMs</div>
          <AgmPicker
            :available-agms="wizardAvailableAgms"
            :selected-agms="wizardSelectedAgms"
            :active-run-agm-ids="activeRunAgmIds"
            @update:available-agms="wizardAvailableAgms = $event"
            @update:selected-agms="wizardSelectedAgms = $event"
          />
        </q-card-section>

        <!-- Step 4: Broadcast Commands (Live-Tracked) -->
        <q-card-section v-if="wizardStep === 4">
          <div class="wizard-step-title q-mb-md">Broadcast Commands</div>
          <q-markup-table>
            <thead>
              <tr>
                <th class="text-left">
                  <span class="text-bold">Command</span>
                </th>
                <th class="text-right">
                  <span class="text-bold q-pr-sm">Now</span>
                  <q-icon name="fas fa-info-circle">
                    <q-tooltip
                      >Commands to be sent to AGMs when this pig run is
                      saved.</q-tooltip
                    >
                  </q-icon>
                </th>
                <th class="text-right">
                  <span class="text-bold q-pr-sm">Launch</span>
                  <q-icon name="fas fa-info-circle">
                    <q-tooltip
                      >Commands to be sent to AGMs when the pig is
                      launched.</q-tooltip
                    >
                  </q-icon>
                </th>
                <th class="text-right">
                  <span class="text-bold q-pr-sm">Receive</span>
                  <q-icon name="fas fa-info-circle">
                    <q-tooltip
                      >Commands to be sent to AGMs when the pig run is
                      ended/saved.</q-tooltip
                    >
                  </q-icon>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="def in broadcastCommandDefinitions"
                :key="def.broadcastCommandId"
              >
                <td class="text-left">{{ def.label }}</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="
                      wizardBroadcastCommandSelections[
                        broadcastCommandDefinitions.indexOf(def)
                      ].isNow
                    "
                    color="blue-brand"
                    :disable="def.disableNow"
                  />
                </td>
                <td class="text-right">
                  <q-checkbox
                    v-model="
                      wizardBroadcastCommandSelections[
                        broadcastCommandDefinitions.indexOf(def)
                      ].isLaunch
                    "
                    color="blue-brand"
                    :disable="def.disableLaunch"
                  />
                </td>
                <td class="text-right">
                  <q-checkbox
                    v-model="
                      wizardBroadcastCommandSelections[
                        broadcastCommandDefinitions.indexOf(def)
                      ].isReceive
                    "
                    color="blue-brand"
                    :disable="def.disableReceive"
                  />
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>

        <!-- Step 6: Notification -->
        <q-card-section v-if="wizardStep === 6">
          <div class="wizard-step-title q-mb-md">Notifications</div>
          <div class="row q-col-gutter-lg">
            <!-- Left Panel: Add Notification -->
            <div class="col-12 col-md-5">
              <div class="wizard-section-title q-mb-sm">
                Add User Notification
              </div>
              <q-select
                v-model="wizardSelectedNotificationUser"
                :options="availableNotificationUsers"
                label="Select User"
                outlined
                dense
                color="blue-brand"
                class="q-mb-md"
                @update:model-value="
                  (val) => {
                    if (val) ensureUserSettings(val.value);
                  }
                "
              />

              <template v-if="wizardSelectedNotificationUser">
                <div class="wizard-section-title q-mb-sm">
                  Notification Channel
                </div>
                <div class="q-gutter-sm q-mb-md">
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].channelEmail
                    "
                    label="Email"
                    color="blue-brand"
                  />
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].channelSms
                    "
                    label="SMS"
                    color="blue-brand"
                  />
                </div>

                <div class="wizard-section-title q-mb-sm">Pig Run Events</div>
                <div class="q-gutter-sm q-mb-md">
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].atLaunch
                    "
                    label="At Launch"
                    color="blue-brand"
                  />
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].atReceive
                    "
                    label="At Receive"
                    color="blue-brand"
                  />
                </div>

                <div class="wizard-section-title q-mb-sm">
                  Marker Passage Events
                </div>
                <div class="q-gutter-sm q-mb-sm">
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].verifiedPassage
                    "
                    label="Verified Passage"
                    color="blue-brand"
                  />
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].manualPassage
                    "
                    label="Manual Passage"
                    color="blue-brand"
                  />
                </div>
                <div class="q-mb-md">
                  <q-checkbox
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].allMarkers
                    "
                    label="All Markers"
                    color="blue-brand"
                  />
                  <q-select
                    v-if="
                      !wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].allMarkers
                    "
                    v-model="
                      wizardUserNotificationSettings[
                        wizardSelectedNotificationUser.value
                      ].selectedMarkers
                    "
                    :options="wizardNotificationMarkers"
                    label="Select Markers"
                    outlined
                    dense
                    multiple
                    use-chips
                    color="blue-brand"
                    class="q-mt-sm"
                  />
                </div>

                <q-btn
                  no-caps
                  class="btn-brand"
                  label="Add"
                  icon="add"
                  :disable="!canAddNotificationUser()"
                  @click="onAddNotificationUser"
                >
                  <q-tooltip v-if="!canAddNotificationUser()">
                    Select at least one notification channel (Email or SMS)
                  </q-tooltip>
                </q-btn>
              </template>
            </div>

            <!-- Right Panel: Configured Notifications -->
            <div class="col-12 col-md-7">
              <div class="wizard-section-title q-mb-sm">
                Configured Notifications
                <q-badge
                  v-if="addedNotificationUsers.length > 0"
                  :label="addedNotificationUsers.length"
                  color="blue-brand"
                  class="q-ml-sm"
                />
              </div>

              <div
                v-if="addedNotificationUsers.length === 0"
                class="flex items-center justify-center text-grey-5 text-caption text-center"
                style="
                  min-height: 120px;
                  border: 1px dashed #ccc;
                  border-radius: 4px;
                "
              >
                No notifications configured. Select a user and click Add.
              </div>

              <q-list v-else bordered separator class="rounded-borders">
                <template
                  v-for="userId in addedNotificationUsers"
                  :key="'notif-' + userId"
                >
                  <q-item>
                    <q-item-section>
                      <q-item-label class="text-weight-medium">
                        {{ getNotificationUserName(userId) }}
                      </q-item-label>
                      <q-item-label caption>
                        <q-badge
                          v-for="(chip, idx) in getNotificationSummaryChips(
                            userId
                          )"
                          :key="idx"
                          :color="chip.color"
                          :label="chip.label"
                          class="q-mr-xs q-mt-xs"
                          outline
                        />
                      </q-item-label>
                    </q-item-section>
                    <q-item-section side>
                      <div class="row q-gutter-xs">
                        <q-btn
                          flat
                          dense
                          round
                          :icon="
                            editingNotificationUserId === userId
                              ? 'check'
                              : 'edit'
                          "
                          :color="
                            editingNotificationUserId === userId
                              ? 'positive'
                              : 'grey-7'
                          "
                          size="sm"
                          @click="onEditNotificationUser(userId)"
                        >
                          <q-tooltip>{{
                            editingNotificationUserId === userId
                              ? 'Done editing'
                              : 'Edit'
                          }}</q-tooltip>
                        </q-btn>
                        <q-btn
                          flat
                          dense
                          round
                          icon="delete"
                          color="negative"
                          size="sm"
                          @click="onRemoveNotificationUser(userId)"
                        >
                          <q-tooltip>Remove</q-tooltip>
                        </q-btn>
                      </div>
                    </q-item-section>
                  </q-item>

                  <!-- Inline Edit Section -->
                  <q-slide-transition>
                    <div
                      v-if="editingNotificationUserId === userId"
                      class="q-pa-md"
                      style="background: #f8f9fa"
                    >
                      <div class="wizard-section-title q-mb-sm">
                        Notification Channel
                      </div>
                      <div class="q-gutter-sm q-mb-md">
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].channelEmail
                          "
                          label="Email"
                          color="blue-brand"
                        />
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].channelSms
                          "
                          label="SMS"
                          color="blue-brand"
                        />
                      </div>

                      <div class="wizard-section-title q-mb-sm">
                        Pig Run Events
                      </div>
                      <div class="q-gutter-sm q-mb-md">
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].atLaunch
                          "
                          label="At Launch"
                          color="blue-brand"
                        />
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].atReceive
                          "
                          label="At Receive"
                          color="blue-brand"
                        />
                      </div>

                      <div class="wizard-section-title q-mb-sm">
                        Marker Passage Events
                      </div>
                      <div class="q-gutter-sm q-mb-sm">
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId]
                              .verifiedPassage
                          "
                          label="Verified Passage"
                          color="blue-brand"
                        />
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].manualPassage
                          "
                          label="Manual Passage"
                          color="blue-brand"
                        />
                      </div>
                      <div class="q-mb-sm">
                        <q-checkbox
                          v-model="
                            wizardUserNotificationSettings[userId].allMarkers
                          "
                          label="All Markers"
                          color="blue-brand"
                        />
                        <q-select
                          v-if="
                            !wizardUserNotificationSettings[userId].allMarkers
                          "
                          v-model="
                            wizardUserNotificationSettings[userId]
                              .selectedMarkers
                          "
                          :options="wizardNotificationMarkers"
                          label="Select Markers"
                          outlined
                          dense
                          multiple
                          use-chips
                          color="blue-brand"
                          class="q-mt-sm"
                        />
                      </div>
                    </div>
                  </q-slide-transition>
                </template>
              </q-list>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            v-if="wizardStep > 1"
            no-caps
            class="btn-brand"
            label="Back"
            @click="onWizardBack"
          />
          <q-btn
            v-if="
              wizardStep < 6 &&
              !(wizardStep === 3 && wizardRunType === 'reconstructed')
            "
            no-caps
            class="btn-brand"
            label="Next"
            :disable="wizardStep === 1 && wizardRunType === ''"
            @click="onWizardNext"
          />
          <q-btn
            v-if="
              wizardStep === 6 ||
              (wizardStep === 3 && wizardRunType === 'reconstructed')
            "
            no-caps
            class="btn-brand"
            :label="isEditMode ? 'Update' : 'Save'"
            @click="onWizardSave"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Manage Pipeline Section Dialog -->
    <PipelineSectionManager
      v-model="managePipelineSectionDialog"
      @sections-updated="onPipelineSectionsUpdated"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  BroadcastCommandSelection,
  BROADCAST_COMMAND_DEFINITIONS,
  createDefaultBroadcastCommandSelections,
} from '@/models/BroadcastCommandSelection';
import { PigRunNotification } from '@/models/PigRunNotification';
import { AddPigRunRequest } from '@/models/AddPigRunRequest';
import { UpdatePigRunRequest } from '@/models/UpdatePigRunRequest';
import { ActivePigRun } from '@/models/ActivePigRun';
import PigRunService from '@/services/PigRunService';
import PipelineSectionService from '@/services/PipelineSectionService';
import DeviceService from '@/services/DeviceService';
import UserService from '@/services/UserService';
import PipelineSectionManager from '@/components/PipelineSectionManager.vue';
import AgmPicker from '@/components/AgmPicker.vue';
import L from 'leaflet';

export default defineComponent({
  name: 'PigRunWizard',
  components: {
    PipelineSectionManager,
    AgmPicker,
  },
  emits: ['saved', 'visibility-change'],
  data() {
    return {
      wizardDialog: false,
      wizardRunType: '',
      wizardName: '',
      wizardPipelineSection: null as any,
      wizardJobNumber: '',
      wizardNote: '',
      wizardReconstructedLaunchTimestamp: '',
      wizardReconstructedReceiveTimestamp: '',
      wizardStep: 1,
      wizardMaxStepReached: 1,
      wizardAvailableAgms: [] as any[],
      wizardSelectedAgms: [] as any[],
      wizardBroadcastCommandSelections:
        createDefaultBroadcastCommandSelections() as BroadcastCommandSelection[],
      wizardPigToolName: '',
      wizardSelectedNotificationUser: null as any,
      wizardUserNotificationSettings: {} as Record<string, any>,
      addedNotificationUsers: [] as string[],
      editingNotificationUserId: null as string | null,
      wizardPipelineSectionOptionsLocal: [] as any[],
      wizardNotificationUsersLocal: [] as any[],
      wizardNotificationMarkersLocal: [] as any[],
      managePipelineSectionDialog: false,
      isEditMode: false,
      editingPigRunId: null as number | null,
      previewMap: null as L.Map | null,
      previewMapLayer: null as L.LayerGroup | null,
    };
  },
  computed: {
    activeRunAgmIds(): number[] {
      const pigRuns: ActivePigRun[] = (this.$store as any).getters
        .activePigRunsList;
      const ids = new Set<number>();
      for (const run of pigRuns) {
        if (this.isEditMode && run.pigRunId === this.editingPigRunId) continue;
        for (const device of run.devices) {
          ids.add(device.uid);
        }
      }
      return Array.from(ids);
    },
    wizardStepDefinitions(): Array<{ step: number; label: string }> {
      if (this.wizardRunType === 'reconstructed') {
        return [
          { step: 1, label: 'Run Type' },
          { step: 2, label: 'General' },
          { step: 3, label: 'AGMs' },
        ];
      }
      return [
        { step: 1, label: 'Run Type' },
        { step: 2, label: 'General' },
        { step: 3, label: 'AGMs' },
        { step: 4, label: 'Commands' },
        { step: 6, label: 'Notifications' },
      ];
    },
    broadcastCommandDefinitions(): any[] {
      return BROADCAST_COMMAND_DEFINITIONS;
    },
    wizardPipelineSectionOptions(): any[] {
      return this.wizardPipelineSectionOptionsLocal;
    },
    wizardNotificationUsers(): any[] {
      return this.wizardNotificationUsersLocal;
    },
    availableNotificationUsers(): any[] {
      return this.wizardNotificationUsersLocal.filter(
        (u: any) => !this.addedNotificationUsers.includes(u.value)
      );
    },
    wizardNotificationMarkers(): any[] {
      return this.wizardNotificationMarkersLocal;
    },
    wizardSelectedAgmIds(): number[] | null {
      return this.wizardSelectedAgms.length > 0
        ? this.wizardSelectedAgms.map((a: any) => a.value)
        : null;
    },
    wizardPigRunNotificationsList(): PigRunNotification[] {
      const result: PigRunNotification[] = [];
      for (const userId of this.addedNotificationUsers) {
        const s = this.wizardUserNotificationSettings[userId];
        if (!s) continue;
        const channels: string[] = [];
        if (s.channelEmail) channels.push('EMAIL');
        if (s.channelSms) channels.push('SMS');
        const events: string[] = [];
        if (s.atLaunch) events.push('PIG_RUN_LAUNCH_EVENT');
        if (s.atReceive) events.push('PIG_RUN_RECEIVE_EVENT');
        for (const evt of events) {
          for (const ch of channels) {
            result.push({
              userId: userId,
              notificationEventType: evt,
              notificationChannel: ch,
              markerIdentityId: null,
            });
          }
        }
      }
      return result;
    },
    wizardMarkerNotificationsList(): PigRunNotification[] {
      const result: PigRunNotification[] = [];
      for (const userId of this.addedNotificationUsers) {
        const s = this.wizardUserNotificationSettings[userId];
        if (!s) continue;
        const channels: string[] = [];
        if (s.channelEmail) channels.push('EMAIL');
        if (s.channelSms) channels.push('SMS');
        const events: string[] = [];
        if (s.manualPassage) events.push('MANUAL_PASSAGE_EVENT');
        if (s.verifiedPassage) events.push('VERIFIED_PASSAGE_EVENT');
        if (channels.length === 0 || events.length === 0) continue;
        const markers = s.allMarkers
          ? this.wizardNotificationMarkersLocal
          : s.selectedMarkers;
        for (const marker of markers) {
          for (const evt of events) {
            for (const ch of channels) {
              result.push({
                userId: userId,
                notificationEventType: evt,
                notificationChannel: ch,
                markerIdentityId: marker.value,
              });
            }
          }
        }
      }
      return result;
    },
    wizardAllNotifications(): PigRunNotification[] {
      return [
        ...this.wizardPigRunNotificationsList,
        ...this.wizardMarkerNotificationsList,
      ];
    },
  },
  watch: {
    wizardPipelineSection(newVal: any) {
      if (newVal?.value) {
        this.$nextTick(() => this.plotPipelineSectionPreview(newVal.value));
      } else {
        this.clearPreviewMap();
      }
    },
    wizardStep() {
      // Re-plot when returning to step 2 with a pipeline section selected
      if (this.wizardStep === 2 && this.wizardPipelineSection?.value) {
        this.$nextTick(() =>
          this.plotPipelineSectionPreview(this.wizardPipelineSection.value)
        );
      }
    },
  },
  methods: {
    // --- Public API (called by PigRunManager via $refs) ---
    openNew() {
      this.isEditMode = false;
      this.editingPigRunId = null;
      this.wizardStep = 1;
      this.wizardMaxStepReached = 1;
      this.wizardRunType = '';
      this.wizardName = '';
      this.wizardPipelineSection = null;
      this.wizardJobNumber = '';
      this.wizardNote = '';
      this.wizardReconstructedLaunchTimestamp = '';
      this.wizardReconstructedReceiveTimestamp = '';
      this.wizardSelectedAgms = [];

      DeviceService.getDevices()
        .then((response: any) => {
          this.wizardAvailableAgms = response.data
            .map((d: any) => ({
              label: String(d.uId),
              value: d.uId,
            }))
            .sort((a: any, b: any) => a.value - b.value);
        })
        .catch((error: any) => {
          console.error('Failed to fetch AGMs:', error);
          this.wizardAvailableAgms = [];
        });

      this.wizardBroadcastCommandSelections =
        createDefaultBroadcastCommandSelections();

      this.wizardPigToolName = '';
      this.wizardSelectedNotificationUser = null;
      this.addedNotificationUsers = [];
      this.editingNotificationUserId = null;

      UserService.getUsers()
        .then((response: any) => {
          this.wizardNotificationUsersLocal = response.data
            .map((u: any) => ({
              label: u.name,
              value: u.id,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
          this.initializeWizardNotifications();
        })
        .catch((error: any) => {
          console.error('Failed to fetch users:', error);
          this.wizardNotificationUsersLocal = [];
          this.initializeWizardNotifications();
        });

      this.wizardDialog = true;
      this.$emit('visibility-change', true);
    },

    async openEdit(pigRunId: number) {
      let runData: any;
      try {
        const response = await PigRunService.getPigRun(pigRunId);
        runData = response.data;
      } catch (error) {
        this.$q.notify({
          color: 'negative',
          message: 'Failed to load pig run details.',
        });
        return;
      }

      this.isEditMode = true;
      this.editingPigRunId = pigRunId;

      if (runData.type === 'R') {
        this.wizardRunType = 'reconstructed';
      } else {
        this.wizardRunType = 'live-tracked';
      }

      this.wizardName = runData.name || '';
      this.wizardJobNumber = runData.jobNumber || '';
      this.wizardNote = runData.note || '';

      this.wizardStep = 1;
      this.wizardMaxStepReached = 6;

      // Load pipeline sections and pre-select
      try {
        const psResponse = await PipelineSectionService.getPipelineSections();
        this.wizardPipelineSectionOptionsLocal = psResponse.data
          .map((ps: any) => ({
            label: ps.name,
            value: ps.pipelineSectionId,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));

        if (runData.pipelineSectionId) {
          this.wizardPipelineSection =
            this.wizardPipelineSectionOptionsLocal.find(
              (opt: any) => opt.value === runData.pipelineSectionId
            ) || null;
        }
      } catch (error) {
        console.error('Failed to fetch pipeline sections:', error);
        this.wizardPipelineSectionOptionsLocal = [];
      }

      // Load AGMs and pre-select
      const existingAgms: number[] = Array.isArray(runData.devices)
        ? runData.devices.map((d: any) => d.uid)
        : [];
      try {
        const devResponse = await DeviceService.getDevices();
        const allAgms = devResponse.data
          .map((d: any) => ({
            label: String(d.uId),
            value: d.uId,
          }))
          .sort((a: any, b: any) => a.value - b.value);

        this.wizardSelectedAgms = allAgms.filter((a: any) =>
          existingAgms.includes(a.value)
        );
        this.wizardAvailableAgms = allAgms.filter(
          (a: any) => !existingAgms.includes(a.value)
        );
      } catch (error) {
        console.error('Failed to fetch AGMs:', error);
        this.wizardAvailableAgms = [];
        this.wizardSelectedAgms = [];
      }

      // Restore broadcast commands
      this.wizardBroadcastCommandSelections =
        createDefaultBroadcastCommandSelections();
      if (
        runData.broadcastCommands &&
        Array.isArray(runData.broadcastCommands)
      ) {
        for (const cmd of runData.broadcastCommands) {
          const idx = this.wizardBroadcastCommandSelections.findIndex(
            (s: BroadcastCommandSelection) =>
              s.broadcastCommandId === cmd.broadcastCommandId
          );
          if (idx !== -1) {
            this.wizardBroadcastCommandSelections[idx].isNow =
              cmd.isNow || false;
            this.wizardBroadcastCommandSelections[idx].isLaunch =
              cmd.isLaunch || false;
            this.wizardBroadcastCommandSelections[idx].isReceive =
              cmd.isReceive || false;
          }
        }
      }

      this.wizardPigToolName = '';
      this.wizardSelectedNotificationUser = null;
      this.addedNotificationUsers = [];
      this.editingNotificationUserId = null;

      // Fetch users
      try {
        const usersResponse = await UserService.getUsers();
        this.wizardNotificationUsersLocal = usersResponse.data
          .map((u: any) => ({
            label: u.name,
            value: u.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
      } catch (error) {
        console.error('Failed to fetch users:', error);
        this.wizardNotificationUsersLocal = [];
      }

      // Load markers if pipeline section is set
      if (runData.pipelineSectionId) {
        try {
          const markersResponse = await PigRunService.getPipelineSectionMarkers(
            runData.pipelineSectionId
          );
          this.wizardNotificationMarkersLocal = markersResponse.data.markers
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .map((m: any) => ({
              label: m.name,
              value: m.markerIdentityId,
            }));
        } catch (error) {
          console.error('Failed to fetch pipeline section markers:', error);
          this.wizardNotificationMarkersLocal = [];
        }
      }

      this.initializeWizardNotifications();

      // Restore notifications from the API response
      if (runData.notifications && Array.isArray(runData.notifications)) {
        const markerIdSet: Record<string, Set<number>> = {};
        const usersWithNotifications = new Set<string>();

        for (const n of runData.notifications) {
          usersWithNotifications.add(n.userId);
        }

        // Batch-create settings for all users with notifications
        const newSettings = { ...this.wizardUserNotificationSettings };
        for (const userId of usersWithNotifications) {
          newSettings[userId] = {
            channelSms: false,
            channelEmail: false,
            atLaunch: false,
            atReceive: false,
            manualPassage: false,
            verifiedPassage: false,
            allMarkers: true,
            selectedMarkers: [],
          };
        }
        this.wizardUserNotificationSettings = newSettings;

        for (const n of runData.notifications) {
          const s = this.wizardUserNotificationSettings[n.userId];

          if (n.notificationChannel === 'EMAIL') s.channelEmail = true;
          if (n.notificationChannel === 'SMS') s.channelSms = true;

          if (n.notificationEventType === 'PIG_RUN_LAUNCH_EVENT')
            s.atLaunch = true;
          if (n.notificationEventType === 'PIG_RUN_RECEIVE_EVENT')
            s.atReceive = true;
          if (n.notificationEventType === 'MANUAL_PASSAGE_EVENT')
            s.manualPassage = true;
          if (n.notificationEventType === 'VERIFIED_PASSAGE_EVENT')
            s.verifiedPassage = true;

          if (n.markerIdentityId) {
            if (!markerIdSet[n.userId]) markerIdSet[n.userId] = new Set();
            markerIdSet[n.userId].add(n.markerIdentityId);
          }
        }

        for (const userId of usersWithNotifications) {
          if (!this.addedNotificationUsers.includes(userId)) {
            this.addedNotificationUsers.push(userId);
          }
        }

        for (const userId of usersWithNotifications) {
          const s = this.wizardUserNotificationSettings[userId];
          if (!s) continue;
          const userMarkerIds = markerIdSet[userId];
          if (!userMarkerIds || userMarkerIds.size === 0) continue;
          const allMarkerValues = this.wizardNotificationMarkersLocal.map(
            (m: any) => m.value
          );
          const hasAll =
            allMarkerValues.length > 0 &&
            allMarkerValues.every((v: number) => userMarkerIds.has(v));
          if (hasAll) {
            s.allMarkers = true;
            s.selectedMarkers = [];
          } else {
            s.allMarkers = false;
            s.selectedMarkers = this.wizardNotificationMarkersLocal.filter(
              (m: any) => userMarkerIds.has(m.value)
            );
          }
        }
      }

      this.wizardDialog = true;
      this.$emit('visibility-change', true);
    },

    // --- Wizard Navigation ---
    onWizardNext() {
      if (this.wizardStep === 2 && !this.wizardPipelineSection) {
        this.$q.notify({
          color: 'warning',
          message: 'A pipeline must be selected before proceeding.',
        });
        return;
      }
      if (this.wizardStep === 2) {
        const name = this.wizardName?.trim();
        if (!name) {
          this.$q.notify({
            color: 'warning',
            message: 'Name is required.',
          });
          return;
        }
        if (name.length > 500) {
          this.$q.notify({
            color: 'warning',
            message: 'Name must be 500 characters or less.',
          });
          return;
        }
      }
      if (this.wizardStep === 1) {
        PipelineSectionService.getPipelineSections()
          .then((response: any) => {
            this.wizardPipelineSectionOptionsLocal = response.data
              .map((ps: any) => ({
                label: ps.name,
                value: ps.pipelineSectionId,
              }))
              .sort((a: any, b: any) => a.label.localeCompare(b.label));
          })
          .catch((error: any) => {
            console.error('Failed to fetch pipeline sections:', error);
            this.wizardPipelineSectionOptionsLocal = [];
          });
      }
      if (this.wizardStep === 4) {
        const pipelineSectionId = this.wizardPipelineSection?.value;
        if (pipelineSectionId && !this.isEditMode) {
          PigRunService.getPipelineSectionMarkers(pipelineSectionId)
            .then((response: any) => {
              this.wizardNotificationMarkersLocal = response.data.markers
                .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
                .map((m: any) => ({
                  label: m.name,
                  value: m.markerIdentityId,
                }));
            })
            .catch((error: any) => {
              console.error('Failed to fetch pipeline section markers:', error);
              this.wizardNotificationMarkersLocal = [];
            });
        }
        this.wizardStep = 6;
      } else {
        this.wizardStep++;
      }
      if (this.wizardStep > this.wizardMaxStepReached) {
        this.wizardMaxStepReached = this.wizardStep;
      }
    },
    onWizardBack() {
      if (this.wizardStep === 6) {
        this.wizardStep = 4;
      } else {
        this.wizardStep--;
      }
    },
    onWizardGoToStep(step: number) {
      if (step > this.wizardMaxStepReached) return;
      if (step === 2 && this.wizardStep < 2) {
        PipelineSectionService.getPipelineSections()
          .then((response: any) => {
            this.wizardPipelineSectionOptionsLocal = response.data
              .map((ps: any) => ({
                label: ps.name,
                value: ps.pipelineSectionId,
              }))
              .sort((a: any, b: any) => a.label.localeCompare(b.label));
          })
          .catch((error: any) => {
            console.error('Failed to fetch pipeline sections:', error);
            this.wizardPipelineSectionOptionsLocal = [];
          });
      }
      if (step === 6 && this.wizardStep < 6 && !this.isEditMode) {
        const pipelineSectionId = this.wizardPipelineSection?.value;
        if (pipelineSectionId) {
          PigRunService.getPipelineSectionMarkers(pipelineSectionId)
            .then((response: any) => {
              this.wizardNotificationMarkersLocal = response.data.markers
                .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
                .map((m: any) => ({
                  label: m.name,
                  value: m.markerIdentityId,
                }));
            })
            .catch((error: any) => {
              console.error('Failed to fetch pipeline section markers:', error);
              this.wizardNotificationMarkersLocal = [];
            });
        }
      }
      this.wizardStep = step;
    },
    isStepCompleted(step: number): boolean {
      if (step === this.wizardStep) return false;
      const defs = this.wizardStepDefinitions;
      const currentIdx = defs.findIndex((d: any) => d.step === this.wizardStep);
      const stepIdx = defs.findIndex((d: any) => d.step === step);
      return stepIdx < currentIdx;
    },
    onWizardDialogHide() {
      this.destroyPreviewMap();
      this.isEditMode = false;
      this.editingPigRunId = null;
      this.$emit('visibility-change', false);
    },
    onManagePipelineSectionOpen() {
      this.managePipelineSectionDialog = true;
    },
    onPipelineSectionsUpdated(options: any[]) {
      this.wizardPipelineSectionOptionsLocal = options;
    },

    // --- Preview Map ---
    initPreviewMap() {
      if (this.previewMap) return;
      const el =
        (this.$refs.wizardPreviewMap as HTMLElement) ||
        (this.$refs.wizardPreviewMapR as HTMLElement);
      if (!el) return;

      this.previewMap = L.map(el, {
        zoomControl: true,
        attributionControl: false,
      }).setView([0, 0], 2);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.previewMap as L.Map);

      this.previewMapLayer = L.layerGroup().addTo(this.previewMap as L.Map);
    },

    clearPreviewMap() {
      if (this.previewMapLayer) {
        this.previewMapLayer.clearLayers();
      }
    },

    destroyPreviewMap() {
      if (this.previewMap) {
        this.previewMap.remove();
        this.previewMap = null;
        this.previewMapLayer = null;
      }
    },

    async plotPipelineSectionPreview(pipelineSectionId: number) {
      this.destroyPreviewMap();
      await this.$nextTick();
      this.initPreviewMap();
      this.clearPreviewMap();
      if (!this.previewMap || !this.previewMapLayer) return;

      try {
        const response = await PigRunService.getPipelineSectionMarkers(
          pipelineSectionId
        );
        const markers = (response.data.markers || []).sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );

        const latLngs: L.LatLng[] = [];
        for (const m of markers) {
          if (!m.latitude || !m.longitude) continue;
          const latlng = L.latLng(m.latitude, m.longitude);
          latLngs.push(latlng);

          L.marker(latlng)
            .bindTooltip(m.name, {
              permanent: false,
              direction: 'top',
              offset: [0, -8],
            })
            .addTo(this.previewMapLayer as L.LayerGroup);
        }

        if (latLngs.length > 1) {
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 2,
            dashArray: '6 4',
          }).addTo(this.previewMapLayer as L.LayerGroup);
        }

        setTimeout(() => {
          if (!this.previewMap) return;
          this.previewMap.invalidateSize();
          if (latLngs.length > 1) {
            this.previewMap.fitBounds(L.latLngBounds(latLngs), {
              padding: [20, 20],
            });
          } else if (latLngs.length === 1) {
            this.previewMap.setView(latLngs[0], 14);
          }
        }, 150);
      } catch (error) {
        console.error('Failed to fetch pipeline section markers:', error);
      }
    },

    // --- Notification Initialization ---
    getDefaultNotificationSettings() {
      return {
        channelSms: false,
        channelEmail: false,
        atLaunch: true,
        atReceive: true,
        manualPassage: true,
        verifiedPassage: true,
        allMarkers: true,
        selectedMarkers: [],
      };
    },
    initializeWizardNotifications() {
      this.wizardUserNotificationSettings = {};
      this.addedNotificationUsers = [];
      this.editingNotificationUserId = null;
    },
    ensureUserSettings(userId: string) {
      if (!this.wizardUserNotificationSettings[userId]) {
        this.wizardUserNotificationSettings = {
          ...this.wizardUserNotificationSettings,
          [userId]: this.getDefaultNotificationSettings(),
        };
      }
    },
    onAddNotificationUser() {
      if (!this.wizardSelectedNotificationUser) return;
      const userId = this.wizardSelectedNotificationUser.value;
      if (this.addedNotificationUsers.includes(userId)) return;
      this.ensureUserSettings(userId);
      this.addedNotificationUsers.push(userId);
      this.wizardSelectedNotificationUser = null;
    },
    onRemoveNotificationUser(userId: string) {
      this.addedNotificationUsers = this.addedNotificationUsers.filter(
        (id: string) => id !== userId
      );
      const { [userId]: _, ...rest } = this.wizardUserNotificationSettings;
      this.wizardUserNotificationSettings = rest;
      if (this.editingNotificationUserId === userId) {
        this.editingNotificationUserId = null;
      }
    },
    onEditNotificationUser(userId: string) {
      this.editingNotificationUserId =
        this.editingNotificationUserId === userId ? null : userId;
    },
    getNotificationUserName(userId: string): string {
      const user = this.wizardNotificationUsersLocal.find(
        (u: any) => u.value === userId
      );
      return user ? user.label : userId;
    },
    getNotificationSummaryChips(
      userId: string
    ): Array<{ label: string; color: string }> {
      const s = this.wizardUserNotificationSettings[userId];
      if (!s) return [];
      const chips: Array<{ label: string; color: string }> = [];
      if (s.channelEmail) chips.push({ label: 'Email', color: 'blue-7' });
      if (s.channelSms) chips.push({ label: 'SMS', color: 'teal-7' });
      if (s.atLaunch) chips.push({ label: 'Launch', color: 'orange-8' });
      if (s.atReceive) chips.push({ label: 'Receive', color: 'orange-8' });
      if (s.verifiedPassage)
        chips.push({ label: 'Verified', color: 'purple-6' });
      if (s.manualPassage) chips.push({ label: 'Manual', color: 'purple-6' });
      if (s.allMarkers) {
        chips.push({ label: 'All Markers', color: 'grey-7' });
      } else if (s.selectedMarkers?.length) {
        chips.push({
          label: `${s.selectedMarkers.length} Marker${
            s.selectedMarkers.length > 1 ? 's' : ''
          }`,
          color: 'grey-7',
        });
      }
      return chips;
    },
    canAddNotificationUser(): boolean {
      if (!this.wizardSelectedNotificationUser) return false;
      const userId = this.wizardSelectedNotificationUser.value;
      const s = this.wizardUserNotificationSettings[userId];
      if (!s) return true; // defaults will have channels unchecked, but we allow adding
      return s.channelEmail || s.channelSms;
    },

    // --- Save / Update Pig Run ---
    async onWizardSave() {
      // Warn if the user configured a notification recipient but never clicked
      // "Add" — without Add, the user is silently dropped from the payload.
      if (
        this.wizardSelectedNotificationUser &&
        this.canAddNotificationUser()
      ) {
        const userLabel =
          this.wizardSelectedNotificationUser.label || 'the selected user';
        const proceed = await new Promise<boolean>((resolve) => {
          this.$q
            .dialog({
              title: 'Unsaved notification recipient',
              message: `You configured notifications for ${userLabel} but didn't click "Add". They will not receive notifications unless you add them. Continue saving without this recipient?`,
              cancel: { label: 'Go back', flat: true },
              ok: { label: 'Save anyway', color: 'negative' },
              persistent: true,
            })
            .onOk(() => resolve(true))
            .onCancel(() => resolve(false));
        });
        if (!proceed) return;
      }

      const type = this.wizardRunType === 'reconstructed' ? 'R' : 'LT';
      const jobNumber = this.wizardJobNumber?.trim() || null;
      const note = this.wizardNote?.trim() || null;

      const activeBroadcastCommands =
        this.wizardBroadcastCommandSelections.filter(
          (cmd: BroadcastCommandSelection) =>
            cmd.isNow || cmd.isLaunch || cmd.isReceive
        );
      const broadcastCommands =
        activeBroadcastCommands.length > 0 ? activeBroadcastCommands : null;

      const notifications =
        this.wizardAllNotifications.length > 0
          ? this.wizardAllNotifications
          : null;

      if (this.isEditMode && this.editingPigRunId != null) {
        const request: UpdatePigRunRequest = {
          pigRunId: this.editingPigRunId,
          type,
          name: this.wizardName,
          pipelineSectionId: this.wizardPipelineSection?.value,
          jobNumber,
          note,
          agms: this.wizardSelectedAgmIds,
          broadcastCommands,
          notifications,
        };

        try {
          await PigRunService.updatePigRun(request);
          this.wizardDialog = false;
          this.isEditMode = false;
          this.editingPigRunId = null;
          this.$emit('saved');
          this.$q.notify({
            color: 'positive',
            message: 'Pig run updated successfully.',
          });
        } catch (error) {
          const err = error as any;
          const message =
            err?.response?.data?.message ||
            err?.message ||
            'An unexpected error occurred.';
          this.$q.notify({
            color: 'negative',
            message: `Failed to update pig run: ${message}`,
          });
        }
      } else {
        const request: AddPigRunRequest = {
          type,
          name: this.wizardName,
          pipelineSectionId: this.wizardPipelineSection?.value,
          jobNumber,
          note,
          agms: this.wizardSelectedAgmIds,
          broadcastCommands,
          notifications,
        };

        try {
          await PigRunService.createPigRun(request);
          this.wizardDialog = false;
          this.$emit('saved');
          this.$q.notify({
            color: 'positive',
            message: 'Pig run created successfully.',
          });
        } catch (error) {
          const err = error as any;
          const message =
            err?.response?.data?.message ||
            err?.message ||
            'An unexpected error occurred.';
          this.$q.notify({
            color: 'negative',
            message: `Failed to create pig run: ${message}`,
          });
        }
      }
    },
  },
});
</script>

<style scoped>
.wizard-card {
  font-family: Roboto, -apple-system, 'Helvetica Neue', Helvetica, Arial,
    sans-serif;
  font-size: 13px;
  font-weight: 400;
}

.wizard-dialog-title {
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #24337a;
}

.wizard-step-title {
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  color: #24337a;
}

.wizard-section-title {
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: #424242;
}

.wizard-body-text {
  font-family: inherit;
  font-size: 13px;
  font-weight: 400;
}

.wizard-card :deep(.q-radio__label) {
  font-family: inherit;
  font-size: 13px;
}

.wizard-card :deep(.q-field__label) {
  font-family: inherit;
}

.wizard-card :deep(.q-field__native) {
  font-family: inherit;
  font-size: 13px;
}

.wizard-card :deep(.q-btn__content) {
  font-family: inherit;
}

/* Wizard Stepper */
.wizard-stepper {
  user-select: none;
}

.wizard-stepper-item {
  min-width: 72px;
  max-width: 100px;
  transition: opacity 0.2s;
}

.wizard-stepper-item.wizard-stepper-clickable {
  cursor: pointer;
}

.wizard-stepper-item.wizard-stepper-disabled {
  opacity: 0.35;
  cursor: default;
}

.wizard-stepper-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: #e0e0e0;
  color: #757575;
  transition: all 0.2s;
}

.wizard-stepper-active .wizard-stepper-circle {
  background: #24337a;
  color: #fff;
}

.wizard-stepper-completed .wizard-stepper-circle {
  background: #43a047;
  color: #fff;
}

.wizard-stepper-label {
  font-size: 10px;
  font-weight: 500;
  color: #9e9e9e;
  text-align: center;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.wizard-stepper-active .wizard-stepper-label {
  color: #24337a;
  font-weight: 600;
}

.wizard-stepper-completed .wizard-stepper-label {
  color: #43a047;
}

.wizard-stepper-line {
  flex: 1;
  height: 2px;
  min-width: 24px;
  background: #e0e0e0;
  margin: 0 4px;
  margin-bottom: 18px;
  transition: background 0.2s;
}

.wizard-stepper-line-completed {
  background: #43a047;
}
</style>
